import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { Webinar } from '@/features/webinar/types';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { fetchWorkspaceTeam } from '@/features/workspace/workspaceThunks';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  createModerator,
  deleteModerator,
  updateModerator,
} from '@/features/webinar/webinarThunks';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Loader from '@/components/ui/Loader';
import { toast } from 'react-toastify';
import type { AppError } from '@/features/auth/types';
import { mapHttpError } from '@/lib/mapApiError';
import { TextFieldWithLimit } from '@/components/ui/TextFieldWithLimit';

interface ModeratorsFormProps {
  webinar?: Webinar | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
  // En création, ouvre la sous-section suivante (Invité) ; la redirection finale est gérée par le parent.
  onSuccess?: () => void;
}

const MAX_OVERRIDE_FULL_NAME_LENGTH = 30;

const moderatorItemSchema = z.object({
  moderatorId: z.number().optional(),
  userId: z.string().min(1),
  overrideFullName: z.string().max(MAX_OVERRIDE_FULL_NAME_LENGTH).optional(),
});

type ModeratorFormItem = z.infer<typeof moderatorItemSchema>;
type ModeratorsFormValues = {
  moderators: ModeratorFormItem[];
};

export default function ModeratorsForm({
  webinar,
  isEdit = false,
  onSaveSuccess,
  onSuccess,
}: ModeratorsFormProps) {
  const { t } = useTranslation('createWebinar');
  const { t: tCommon } = useTranslation('common');
  const dispatch = useAppDispatch();
  const workspaceId = useAppSelector((state) => state.workspace.currentWorkspace?.id);
  const team = useAppSelector((state) => state.workspace.team);
  const isEvergreenConfiguration = useAppSelector(
    (state) => state.webinar.currentSessionConfiguration?.type === 'EVERGREEN',
  );
  const presenters = useAppSelector((state) => state.webinar.presenters);
  const allModerators = useAppSelector((state) => state.webinar.moderators);
  const webinarModerators = useMemo(
    () => allModerators.filter((m) => (webinar?.id ? m.webinarId === webinar.id : false)),
    [allModerators, webinar?.id],
  );
  const webinarPresenters = useMemo(
    () => (presenters ?? []).filter((p) => (webinar?.id ? p.webinarId === webinar.id : false)),
    [presenters, webinar?.id],
  );

  useEffect(() => {
    if (workspaceId) {
      void dispatch(fetchWorkspaceTeam({ workspaceId, page: 0, size: 50 }));
    }
  }, [workspaceId, dispatch]);

  const initialModeratorIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    initialModeratorIdsRef.current = new Set(webinarModerators.map((m) => m.id));
  }, [webinarModerators]);

  const getTeamMemberDisplayName = (userId: string) => {
    const member = team.find((m) => m.userId === userId);
    if (!member) return userId;
    return `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim() || userId;
  };

  const formModerators = useMemo(
    () =>
      webinarModerators.map<ModeratorFormItem>((m) => ({
        moderatorId: m.id,
        userId: m.userId,
        overrideFullName: m.overrideFullName?.trim()
          ? m.overrideFullName
          : getTeamMemberDisplayName(m.userId),
      })),
    [webinarModerators, team],
  );

  const lastResetValueRef = useRef<string>('');

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<ModeratorsFormValues>({
    resolver: zodResolver(
      z.object({
        moderators: z.array(moderatorItemSchema),
      }),
    ),
    defaultValues: {
      moderators: formModerators,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!webinar?.id) return;

    const currentValue = `${webinar.id}:${JSON.stringify(formModerators)}`;
    if (lastResetValueRef.current !== currentValue) {
      lastResetValueRef.current = currentValue;
      reset({
        moderators: formModerators,
      });
    }
  }, [webinar?.id, formModerators, reset, isSubmitting]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'moderators',
  });

  const presenterUserIds = useMemo(
    () => new Set(webinarPresenters.map((p) => p.userId)),
    [webinarPresenters],
  );
  const availableTeamForSelect = useMemo(
    () =>
      team.filter((member) => {
        if (fields.some((f) => f.userId === member.userId)) return false;
        if (isEvergreenConfiguration) return true;
        return !presenterUserIds.has(member.userId);
      }),
    [team, presenterUserIds, fields, isEvergreenConfiguration],
  );

  const buildModeratorInput = (item: ModeratorFormItem) => {
    const memberDisplayName = getTeamMemberDisplayName(item.userId);
    const trimmedOverride = item.overrideFullName?.trim() ?? '';
    const hasCustomOverride = trimmedOverride.length > 0 && trimmedOverride !== memberDisplayName;

    return {
      jobTitle: t('moderatorsForm.jobTitle'),
      userId: item.userId,
      overrideFullName: hasCustomOverride ? trimmedOverride : undefined,
    };
  };

  const handleModeratorError = (error: AppError | undefined, fallbackMessage: string) => {
    const isUserAlreadyPresenter =
      error?.details &&
      Array.isArray(error.details) &&
      error.details.some(
        (detail: { message?: string; fieldName?: string }) =>
          detail.message === 'HTTP.ERROR.400.USER.ALREADY_ASSIGNED_TO_ROLE' &&
          detail.fieldName === 'userId',
      );

    const mainMessage = isUserAlreadyPresenter
      ? t('moderatorsForm.validation.alreadyPresenter')
      : error?.message
        ? tCommon(error.message, error.message)
        : tCommon('errors.generic', fallbackMessage);
    const rawDetails =
      !isUserAlreadyPresenter && Array.isArray(error?.details) ? error.details : [];
    const detailMessages = rawDetails
      .map((d: { message?: string }) =>
        d.message ? tCommon(mapHttpError(d.message), d.message) : '',
      )
      .filter(Boolean);
    toast.error([mainMessage, ...detailMessages].join(' • '));
  };

  const onSubmit = async (data: ModeratorsFormValues) => {
    if (!workspaceId || !webinar?.id) return;

    const currentModeratorIds = new Set(
      data.moderators
        .map((item) => item.moderatorId)
        .filter((id): id is number => id !== undefined),
    );

    const moderatorsToDelete = Array.from(initialModeratorIdsRef.current).filter(
      (id) => !currentModeratorIds.has(id),
    );

    for (const moderatorIdToDelete of moderatorsToDelete) {
      const deleteResult = await dispatch(
        deleteModerator({
          workspaceId,
          webinarId: webinar.id,
          moderatorId: moderatorIdToDelete,
        }),
      );

      if (deleteModerator.rejected.match(deleteResult)) {
        const error = deleteResult.payload as AppError | undefined;
        if (error?.message) {
          const mainMessage = tCommon(error.message, error.message);
          toast.error(mainMessage);
        } else {
          toast.error(tCommon('errors.generic', 'Une erreur est survenue lors de la suppression.'));
        }

        if (error?.details && Array.isArray(error.details)) {
          error.details.forEach((detail: { message?: string; fieldName?: string }) => {
            if (detail.message) {
              const mappedMessage = mapHttpError(detail.message);
              const translatedMessage = tCommon(mappedMessage, detail.message);
              toast.error(translatedMessage);
            }
          });
        }
        return;
      }
    }

    for (const item of data.moderators) {
      const input = buildModeratorInput(item);

      if (!item.moderatorId) {
        const createResult = await dispatch(
          createModerator({
            workspaceId,
            webinarId: webinar.id,
            input,
          }),
        );

        if (createModerator.rejected.match(createResult)) {
          handleModeratorError(
            createResult.payload as AppError | undefined,
            'Une erreur est survenue lors de la création.',
          );
          return;
        }
        continue;
      }

      const existingModerator = webinarModerators.find((m) => m.id === item.moderatorId);
      const existingOverride = existingModerator?.overrideFullName?.trim() ?? '';
      const nextOverride = input.overrideFullName?.trim() ?? '';

      if (existingOverride !== nextOverride) {
        const updateResult = await dispatch(
          updateModerator({
            workspaceId,
            webinarId: webinar.id,
            moderatorId: item.moderatorId,
            input,
          }),
        );

        if (updateModerator.rejected.match(updateResult)) {
          handleModeratorError(
            updateResult.payload as AppError | undefined,
            'Une erreur est survenue lors de la mise à jour.',
          );
          return;
        }
      }
    }

    toast.success(t('moderatorsForm.saveSuccess'));

    if (isEdit && onSaveSuccess) {
      onSaveSuccess();
    } else if (!isEdit) {
      onSuccess?.();
    }
  };

  const handleAddModerator = (userId: string) => {
    const isAlreadyPresent = fields.some((field) => field.userId === userId);
    if (isAlreadyPresent) {
      return;
    }
    append({
      moderatorId: undefined,
      userId,
      overrideFullName: getTeamMemberDisplayName(userId),
    });
  };

  const handleRemoveModerator = (index: number) => {
    remove(index);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <Select onValueChange={handleAddModerator}>
          <SelectTrigger label={t('moderatorsForm.addModerator')}>
            <SelectValue placeholder={t('moderatorsForm.addModerator')} />
          </SelectTrigger>
          <SelectContent>
            {availableTeamForSelect.map((member) => (
              <SelectItem key={member.userId} value={member.userId}>
                {member.firstName} {member.lastName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex flex-col gap-4">
          {fields.map((field, index) => {
            const memberDisplayName = getTeamMemberDisplayName(field.userId);
            const overrideFullNameValue = watch(`moderators.${index}.overrideFullName`) ?? '';

            return (
              <div key={field.id} className="flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-1 items-center justify-between rounded-md border bg-white border-input px-4">
                    <div className="flex flex-col justify-center h-12">
                      <span className="text-base">{memberDisplayName}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => handleRemoveModerator(index)}
                    className="p-2 h-12 shrink-0 cursor-pointer border rounded-lg border-input bg-[#FBFBFB] disabled:cursor-not-allowed disabled:bg-white disabled:border-input"
                  >
                    <X className="size-5 text-red-500" />
                  </button>
                </div>

                <TextFieldWithLimit
                  label={t('moderatorsForm.overrideFullName')}
                  value={overrideFullNameValue}
                  placeholder={memberDisplayName}
                  onChange={(v) =>
                    setValue(`moderators.${index}.overrideFullName`, v, { shouldDirty: true })
                  }
                  maxLength={MAX_OVERRIDE_FULL_NAME_LENGTH}
                />
              </div>
            );
          })}
        </div>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting && <Loader />}
          {t('save')}
        </Button>
      </div>
    </form>
  );
}
